import React, { useEffect, useRef, useState } from "react";
import List from "../../../components/List/List";
import Pagination from "../../../components/Pagination/Pagination";
import { LicensesBillingStatuses, LicenseStatuses } from "../../../constants/licenses";
import { concatTime, formatDate } from "../../../utils/date";
import { BsCpu } from "react-icons/bs";
import LicenseService from "../../../services/LicensesService";
import { DropdownMenu } from "../../../components/DropdownMenu/DropdownMenu";
import ModalComponent from "../../../components/Modal/ModalComponent";
import Input from "../../../components/Input/Input";
import { UnitSelector } from "../../../components/UnitSelector/UnitSelector";
import { BiTransfer } from "react-icons/bi";
import { MdBlock, MdOutlineContentCopy } from "react-icons/md";
import { NotificationManager } from "react-notifications";
import { PiHandCoins, PiHandDeposit } from "react-icons/pi";
import { LuEye, LuReplace } from "react-icons/lu";
import { useLicenseDetails } from "../../../contexts/licenseDetails";
import { LicenseTechnicalStatus } from "../../../components/LicenseTechnicalStatus/LicenseTechnicalStatus";
import { LicenseExpirationDate } from "../../../components/LicenseExpirationDate/LicenseExpirationDate";
import { LicenseBillingStatus } from "../../../components/LicenseBillingStatus/LicenseBillingStatus";
import { TbMoneybagPlus, TbReportMoney } from "react-icons/tb";

const ListLicenses = (props) => {
   const [data, setData] = useState([]);
   const [maxPage, setMaxPage] = useState(1);
   const [rowsPerPage, setRowsPerPage] = useState(12);
   const [total, setTotal] = useState(0);
   const [currentPage, setCurrentPage] = useState(1);
   const [collumnClicked, setCollumnClicked] = useState({
      collumnTitle: "id",
      orderDirection: "DESC",
   });
   const cancelTokenRef = useRef(null);
   const [isLoading, setIsLoading] = useState(true);

   const [ownerId, setOwnerId] = useState();
   const [showModalUpdateOwner, setShowModalUpdateOwner] = useState(false);
   const [showModalUpdateSN, setShowModalUpdateSN] = useState(false);
   const [newSerial, setNewSerial] = useState("");
   const [transferReason, setTransferReason] = useState("");
   const [licenseSelected, setLicenseSelected] = useState(null);

   const { openModal } = useLicenseDetails();

   const getLicenses = async (page, filters, collumnClicked) => {
      setIsLoading(true);
      if (cancelTokenRef.current) {
         cancelTokenRef.current.abort();
      }
      cancelTokenRef.current = new AbortController();
      const response = await LicenseService.getPaginatedLicenses(
         page,
         filters,
         collumnClicked,
         rowsPerPage,
         cancelTokenRef.current.signal
      );
      const pagination = response?.data?.pagination ?? response?.data?.meta;
      setIsLoading(false);
      setData(response?.data?.items ?? []);
      setMaxPage(Math.ceil((pagination?.totalItems ?? 0) / rowsPerPage));
      setTotal(pagination?.totalItems ?? 0);
   };

   useEffect(() => {
      getLicenses(currentPage, props.filters, collumnClicked);

      const refreshLicenses = () =>
         getLicenses(currentPage, props.filters, collumnClicked);
      window.addEventListener("license-details-closed", refreshLicenses);
      return () =>
         window.removeEventListener("license-details-closed", refreshLicenses);
   }, [
      props.filters,
      collumnClicked,
      currentPage,
      rowsPerPage,
   ]);

   useEffect(() => {
      setCurrentPage(1);
   }, [props.filters]);

   const openUpdateModal = (license) => {
      if (!license) {
         alert("Selecione uma licença para atualizar o Serial Number.");
         return;
      }
      setNewSerial(license.serialNumber);
      setLicenseSelected(license);
      setShowModalUpdateSN(true);
   };

   const openUpdateOwnerModal = (license) => {
      if (!license) {
         alert("Selecione uma licença para atualizar o proprietário.");
         return;
      }
      setOwnerId(license.currentOwnerUnitId);
      setLicenseSelected(license);
      setShowModalUpdateOwner(true);
   };

   const handleUpdateSN = async () => {
      const response = await LicenseService.updateSerialNumber(
         licenseSelected.id,
         newSerial,
      );
      if (response.status === 201) {
         setShowModalUpdateSN(false);
         setLicenseSelected(null);
         getLicenses(currentPage, props.filters, collumnClicked);
         setNewSerial(null);
      }
   };

   const handleUpdateOwner = async () => {
      const response = await LicenseService.transferToUnit(
         licenseSelected.id,
         ownerId,
         transferReason,
      );
      if (response.data) {
         setShowModalUpdateOwner(false);
         setLicenseSelected(null);
         getLicenses(currentPage, props.filters, collumnClicked);
      }
   };

   return (
      <>
         <div className="list-container list-visibility">
            <List
               isLoading={isLoading}
               selectableRows={false}
               handleCollumnTitleClick={(collumnIndex) =>
                  setCollumnClicked(collumnIndex)
               }
               collumsObjIndex={[
                  {
                     collumnLabel: "Licença / Tag",
                     index: "tag",
                     type: "custom",
                     custom: ({ row }) => {
                        return (
                           <span className="inline-flex flex-col gap-2">
                              <span>{row.tag}</span>
                              <span className="opacity-50 text-xs!">{row.externalId}</span>
                           </span>
                        );
                     }
                  },
                  {
                     collumnLabel: "Status",
                     type: "custom",
                     custom: ({ row }) => <LicenseTechnicalStatus license={row} />
                  },
                  {
                     collumnLabel: "Proprietário",
                     type: "custom",
                     custom: ({ row }) => {
                        return row.currentOwnerUnitId && row.owner ? row.owner : <span style={{ opacity: 0.36 }}>Sem proprietário</span>;
                     }
                  },
                  {
                     collumnLabel: "Alocado em",
                     type: "custom",
                     custom: ({ row }) => {
                        return row.allocatedUnitId && row.allocatedUnit ? row.allocatedUnit.name : <span style={{ opacity: 0.36 }}>Não alocado</span>;
                     }
                  },
                  {
                     collumnLabel: "Dispositivo",
                     index: "serialNumber",
                     type: "custom",
                     custom: ({ row }) => {
                        return (
                           <div className="flex flex-col gap-2">
                              <div className="flex flex-row items-center gap-3">
                                 {(row.serialNumber?.trim()?.length > 0) ? (
                                    <span>{row.serialNumber}</span>
                                 ) : (
                                    <span style={{ opacity: 0.36 }}>Sem equipamento</span>
                                 )}
                              </div>
                              <div className="opacity-40 text-xs!">
                                 {row.type.code}
                              </div>
                           </div>
                        );
                     }
                  },
                  {
                     collumnLabel: "Vencimento",
                     index: "end_date",
                     type: "custom",
                     custom: ({ row }) => <LicenseExpirationDate license={row} />
                  },
                  {
                     collumnLabel: "Financeiro",
                     index: "end_date",
                     type: "custom",
                     custom: ({ row }) => <LicenseBillingStatus license={row} />
                  },
                  {
                     collumnLabel: "Ações",
                     index: 'actions',
                     type: "custom",
                     custom: ({ row }) => {
                        return (
                           <DropdownMenu
                              onMouseEnter={e => e.stopPropagation()}
                              onClick={e => e.stopPropagation()}
                              content={!row.hasOwnerAccess ? (
                                 <div className="p-1! flex flex-row items-center gap-2">
                                    <MdBlock size={18} />
                                    Sem permissão em {row.owner}
                                 </div>
                              ) : null}
                              options={row.hasOwnerAccess ? [
                                 {
                                    icon: LuEye,
                                    label: "Ver detalhes",
                                    onClick: () => {
                                       openModal(row, "overview");
                                    },
                                 },
                                 {
                                    icon: MdOutlineContentCopy,
                                    label: "Copiar token",
                                    onClick: () => {
                                       navigator.clipboard.writeText(row.tokenMqtt);
                                       NotificationManager.success("", "Token copiado com sucesso!", 3000);
                                    },
                                 },
                                 {
                                    icon: BsCpu,
                                    label: "Atualizar Serial Number",
                                    onClick: () => {
                                       openUpdateModal(row);
                                    },
                                 },
                                 {
                                    icon: BiTransfer,
                                    label: "Transferir",
                                    onClick: () => {
                                       openUpdateOwnerModal(row);
                                    },
                                 },
                                 {
                                    icon: LuReplace,
                                    label: row.allocatedUnitId ? "Realocar" : "Alocar",
                                    onClick: () => {
                                       openModal(row, "actions", "allocate");
                                    },
                                 },
                                 {
                                    icon: MdBlock,
                                    label: row.technicalStatus === 'BLOCKED' ? "Desbloquear" : "Bloquear",
                                    onClick: () => {
                                       openModal(row, "actions", row.technicalStatus === 'BLOCKED' ? "unblock" : "block");
                                    },
                                 },
                                 {
                                    icon: TbMoneybagPlus,
                                    label: "Registrar pagamento",
                                    onClick: () => {
                                       openModal(row, "billing", "register-payment");
                                    },
                                 },
                                 !row.automaticReadjustment ? {
                                    icon: TbReportMoney,
                                    label: "Nova cobrança",
                                    onClick: () => {
                                       openModal(row, "billing");
                                       openModal(row, "billing", "create-billing");
                                    },
                                 } : null
                              ] : []}
                           />
                        );
                     }
                  }
               ]}
               data={data}
            />
         </div>
         {maxPage > 1 && (
            <Pagination
               currentPage={currentPage}
               rowsPerPage={rowsPerPage}
               maxPage={maxPage}
               total={total}
               changePage={(page) =>
                  setCurrentPage(page)
               }
               changeRowsPerPage={(rowsPerPage) =>
                  setRowsPerPage(rowsPerPage)
               }
            />
         )}
         {/* Modal para atualizar o Serial Number */}
         <ModalComponent
            modalIsOpen={showModalUpdateSN}
            handleConfirm={handleUpdateSN}
            handleCancel={() => setShowModalUpdateSN(false)}
            modalText={
               newSerial ? "Atualizar Serial Number" : "Adicionar Serial Number"
            }
         >
            <div className="modal-input-container">
               <Input
                  type="text"
                  labelText={"Serial Number do Equipamento"}
                  value={newSerial}
                  onChange={({ target }) => setNewSerial(target.value)}
                  placeholder="Digite o Serial Number"
               />
            </div>
         </ModalComponent>
         {/* Modal para transferir */}
         <ModalComponent
            modalIsOpen={showModalUpdateOwner}
            handleConfirm={handleUpdateOwner}
            handleCancel={() => setShowModalUpdateOwner(false)}
            modalText={
               "Transferir para outro proprietário"
            }
         >
            <div className="modal-input-container w-full! gap-3! flex flex-col">
               <UnitSelector
                  modalTitle={"Selecione o proprietário"}
                  inputLabel={"Selecione o proprietário"}
                  parentId={ownerId ? `${ownerId}` : null}
                  setParentId={id => setOwnerId(Number(id))}
               />
               <Input
                  type="text"
                  labelText={"Motivo"}
                  value={transferReason}
                  onChange={({ target }) => setTransferReason(target.value)}
                  placeholder=""
               />
            </div>
         </ModalComponent>
      </>
   );
};

export default ListLicenses;
